"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import { useState } from "react";
import PrivacyPolicyModal from "./PrivacyPolicyModal";
import TermsModal from "./TermsModal";
import CookiePolicyModal from "./CookiePolicyModal";

export default function Footer() {
  const t = useTranslations("footer");
  const tNav = useTranslations("nav");
  const tCommon = useTranslations("common");
  const tCenter = useTranslations("center");
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showCookieModal, setShowCookieModal] = useState(false);

  const scrollToTop = () => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  const footerLinks = {
    courses: {
      title: t("courses"),
      links: [
        {
          label: t("motorcycleCourse"),
          href: "/course/motorcycle",
        },
        {
          label: t("privateGroupCourse"),
          href: "/course/group",
        },
        {
          label: t("automobileCourse"),
          href: "/course/automobile",
        },
      ],
    },
    centers: {
      title: t("center"),
      links: [
        { label: tCenter("samutPrakan"), href: "/center/hsrc?center=samut-prakan" },
        { label: tCenter("bangkok"), href: "/center/hsrc?center=bangkok" },
        { label: tCenter("chiangMai"), href: "/center/hsrc?center=chiang-mai" },
        { label: tCenter("phuket"), href: "/center/hsrc?center=phuket" },
      ],
    },
    knowledge: {
      title: t("knowledge"),
      links: [
        {
          label: tNav("knowledgePdsa"),
          href: "/safetytips/safetyfirst",
        },
        {
          label: tNav("knowledgeBasicRiding"),
          href: "/safetytips/saferiding",
        },
      ],
    },
  };

  const externalLinks = [
    { label: t("thaiHonda"), href: "https://www.thaihonda.co.th/honda/" },
    { label: t("products"), href: "https://www.thaihonda.co.th/honda/motorcycle" },
    { label: t("pressRelease"), href: "https://www.thaihonda.co.th/honda/news" },
    { label: "Honda Bigbike", href: "https://www.thaihonda.co.th/hondabigbike/" },
    { label: "CUB House", href: "https://www.thaihonda.co.th/cubhouse/" },
    { label: "Power Product", href: "https://thpp.thaihonda.co.th/dcs-wbs/load-page.action?request_locale=th_TH" },
  ];

  return (
    <>
      <div className="bg-white w-full">
        <div className="max-w-[1920px] mx-auto px-6 lg:px-[clamp(2rem,5vw,250px)] py-4">
          <div className="flex justify-end">
            <button
              onClick={scrollToTop}
              className="flex flex-col items-center gap-1 text-honda-red hover:text-honda-red-dark transition-colors"
              aria-label="Scroll to top"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 10l7-7m0 0l7 7m-7-7v18"
                />
              </svg>
              <span className="text-sm font-medium">{tNav("scrollToTop")}</span>
            </button>
          </div>
        </div>
      </div>

      <footer className="bg-gray-100 text-gray-800">
        <div className="max-w-[1920px] mx-auto px-6 lg:px-[clamp(2rem,5vw,250px)] pt-12 pb-8">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
            <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
              <p className="text-[22px] text-gray-900 font-bold">Follow us</p>
              <div className="flex gap-3">
                <a href="https://www.facebook.com/HondaSafetyRidingThailand" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-honda-red flex items-center justify-center hover:bg-honda-red-dark transition-colors overflow-hidden">
                  <Image
                    src="/images/icons/icon-fb.svg"
                    alt="Facebook"
                    width={32}
                    height={32}
                    className="w-full h-full object-cover"
                  />
                </a>
                <a href="https://www.tiktok.com/@hondasafetythailand" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-honda-red flex items-center justify-center hover:bg-honda-red-dark transition-colors overflow-hidden">
                  <Image
                    src="/images/icons/icon-tt.svg"
                    alt="TikTok"
                    width={32}
                    height={32}
                    className="w-full h-full object-cover"
                  />
                </a>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-2 md:mt-0">
              <span className="text-lg text-honda-red font-bold">02 757 6111 {tCommon('extLabel')} 3005</span>
              <Image
                src="/images/icons/icon-callcenter-red.svg"
                alt="Call Center"
                width={20}
                height={20}
                className="w-5 h-5 object-contain"
              />
            </div>
          </div>
        </div>

        <div className="border-t border-gray-300 mb-4"></div>

        <div className="max-w-[1920px] mx-auto px-6 lg:px-[clamp(2rem,5vw,250px)]">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            <div>
              <div className="mb-6">
                <h3 className="text-base lg:text-[18px] mb-4 text-gray-900 font-bold">{footerLinks.courses.title}</h3>
                <ul className="space-y-2">
                  {footerLinks.courses.links.map((link) => (
                    <li key={link.href}>
                      <a href={link.href} className="text-sm lg:text-[13px] text-gray-800 hover:text-honda-red transition-colors whitespace-nowrap">
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="hidden lg:block mt-12">
                <h3 className="text-base lg:text-[18px] mb-4 text-gray-900 font-bold">
                  <a href="/newsactivity" className="hover:text-honda-red transition-colors">
                    {t("newsAndActivities")}
                  </a>
                </h3>
                <h3 className="text-base lg:text-[18px] mb-4 text-gray-900 font-bold">
                  <a href="/aboutus" className="hover:text-honda-red transition-colors">
                    {t("aboutHondaSafety")}
                  </a>
                </h3>
              </div>
            </div>

            <div>
              <h3 className="text-base lg:text-[18px] mb-4 text-gray-900 font-bold">{footerLinks.centers.title}</h3>
              <ul className="space-y-2">
                {footerLinks.centers.links.map((link) => (
                  <li key={link.href}>
                    <a href={link.href} className="text-sm lg:text-[13px] text-gray-800 hover:text-honda-red transition-colors whitespace-nowrap">
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-base lg:text-[18px] mb-4 text-gray-900 font-bold">{footerLinks.knowledge.title}</h3>
              <ul className="space-y-2">
                {footerLinks.knowledge.links.map((link) => (
                  <li key={link.href}>
                    <a href={link.href} className="text-sm lg:text-[13px] text-gray-800 hover:text-honda-red transition-colors whitespace-nowrap">
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="block lg:hidden">
              <h3 className="text-base mt-2 text-gray-900 font-bold">
                <a href="/newsactivity" className="block hover:text-honda-red transition-colors">
                  {t("newsAndActivities")}
                </a>
              </h3>
              <h3 className="text-base mt-2 text-gray-900 font-bold">
                <a href="/aboutus" className="block hover:text-honda-red transition-colors">
                  {t("aboutHondaSafety")}
                </a>
              </h3>
            </div>
            <div className="lg:border-l lg:border-gray-300 lg:pl-4">
              <ul className="space-y-2">
                {externalLinks.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="text-sm lg:text-[13px] text-gray-800 hover:text-honda-red transition-colors inline-flex items-center gap-1 whitespace-nowrap"
                      target={link.href.startsWith('http') ? '_blank' : undefined}
                      rel={link.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    >
                      {link.label}
                      <Image
                        src="/images/icons/icon-arrow-red.svg"
                        alt="External link"
                        width={12}
                        height={12}
                        className="w-3 h-3 object-contain"
                      />
                    </a>
                  </li>
                ))}
              </ul>
              <div className="h-6"></div>
            </div>
          </div>
        </div>
      </footer>

      <div className="border-t border-gray-300 bg-gray-100">
        <div className="max-w-[1920px] mx-auto px-6 lg:px-[clamp(2rem,5vw,250px)] py-4">
          <div className="flex items-center justify-start gap-6">
            <button type="button" onClick={() => setShowTermsModal(true)} className="text-base text-gray-800 hover:text-honda-red transition-colors cursor-pointer">
              {t("terms")}
            </button>
            <a href="https://www.thaihonda.co.th/policy/index.aspx" target="_blank" rel="noopener noreferrer" className="text-base text-gray-800 hover:text-honda-red transition-colors cursor-pointer">
              {t("privacyPolicy")}
            </a>
            <button type="button" onClick={() => setShowCookieModal(true)} className="text-base text-gray-800 hover:text-honda-red transition-colors cursor-pointer">
              {t("cookiePolicy")}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-gray-300">
        <div className="max-w-[1920px] mx-auto px-6 lg:px-[clamp(2rem,5vw,250px)] py-4">
          <div className="flex items-center justify-center">
            <p className="text-base text-gray-800">
              {t("copyright")}
            </p>
          </div>
        </div>
      </div>

      <TermsModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />
      <PrivacyPolicyModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} />
      <CookiePolicyModal isOpen={showCookieModal} onClose={() => setShowCookieModal(false)} />
    </>
  );
}